import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import { BS_LIMITS } from 'enums/pitch-design';
import { IConversionResult, IRepertoire } from 'interfaces/i-mlb-browse';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { HawkeyeHelper } from 'lib_ts/classes/hawkeye.helper';
import { FTPS_TO_MPH, FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { MS_LIMITS } from 'lib_ts/enums/machine.enums';
import {
  AVG_EXCLUDED_OUTCOMES,
  MlbOutcomeCode,
  STRIKE_OUTCOMES,
} from 'lib_ts/enums/mlb-stats-api/guid-metadata-types.enum';
import { MLB_TEAMS_DICT } from 'lib_ts/enums/mlb.enums';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import {
  IMlbPitch,
  IMlbPitchExt,
} from 'lib_ts/interfaces/mlb-stats-api/i-pitch';
import {
  MlbLocation,
  MlbReleaseData,
  MlbReleasePosition,
  MlbSeamOrientation,
  MlbSpinVector,
  MlbTrajectoryData,
  MlbVelocity,
} from 'lib_ts/interfaces/mlb-stats-api/raw-results/game-guids-pitch-segment';
import { DEFAULT_PLATE } from 'lib_ts/interfaces/pitches';
import { IPosition, ISpin, IVelocity } from 'lib_ts/interfaces/pitches/i-base';
import { clamp, compact, first, isNil, sum } from 'lodash';
import { Vector3 } from 'three';

/** for components to interact with events */
export class MlbStatsHelper {
  static getPlayerHeadshotUrl(playerPk: number | undefined) {
    if (isNil(playerPk)) {
      return undefined;
    }

    return `https://img.mlbstatic.com/mlb-photos/image/upload/people/${playerPk}/headshot/silo/current.png`;
  }

  static validSpinVector(value: Partial<MlbSpinVector> | undefined): boolean {
    if (!value) {
      return false;
    }

    if ([value.x, value.y, value.z].includes(undefined)) {
      return false;
    }

    return true;
  }

  static validVelocity(value: Partial<MlbVelocity> | undefined): boolean {
    if (!value) {
      return false;
    }

    if ([value.x, value.y, value.z].includes(undefined)) {
      return false;
    }

    return true;
  }

  static validTrajectory(
    value: Partial<MlbTrajectoryData> | undefined
  ): boolean {
    if (!value) {
      return false;
    }

    if (
      [
        value.horizontalBreak,
        value.verticalBreak,
        value.verticalBreakInduced,
      ].includes(undefined)
    ) {
      return false;
    }

    return true;
  }

  static validSeamOrientation(
    value: Partial<MlbSeamOrientation> | undefined
  ): boolean {
    if (!value) {
      return false;
    }

    if (
      [
        value.xx,
        value.xy,
        value.xz,
        value.yx,
        value.yy,
        value.yz,
        value.zx,
        value.zy,
        value.zz,
      ].includes(undefined)
    ) {
      return false;
    }

    return true;
  }

  // checks to ensure a pitch can be built from this play
  static getErrors(value: Partial<IMlbPitch> | undefined): string[] {
    if (!value) {
      return ['Play is undefined'];
    }

    const errors: string[] = [];

    const spin = value.releaseData?.spinVector;
    if (!this.validSpinVector(spin)) {
      errors.push('Invalid spin vector');
    }

    const velo = value.lastMeasuredData?.velocity;
    if (!this.validVelocity(velo)) {
      errors.push('Invalid velocity');
    }

    const traj = value.trajectoryData;
    if (!this.validTrajectory(traj)) {
      errors.push('Invalid trajectory');
    }

    const seams = value.releaseData?.seamOrientation;
    if (!this.validSeamOrientation(seams)) {
      errors.push('Invalid seam orientation');
    }

    return errors;
  }

  // mutates the original values
  static averageReleasesByPitcher(
    pitches: IMlbPitchExt[],
    values: IConversionResult[]
  ): void {
    const guidsDict = ArrayHelper.groupBy(pitches, 'pitcherPk');

    for (const pitcherPk of Object.keys(guidsDict)) {
      const entry = guidsDict[pitcherPk];

      if (!entry) {
        continue;
      }

      const guids = entry.map((g) => g.guid);
      const results = values.filter((r) => guids.includes(r.guid));

      const avg = MiscHelper.getMeanObject(
        compact(
          results.map((r) => {
            if (!r.bs) {
              return undefined;
            }

            const p: IPosition = {
              px: r.bs.px,
              py: r.bs.py,
              pz: r.bs.pz,
            };

            return p;
          })
        )
      ) as IPosition;

      results.forEach((r) => {
        if (!r.bs) {
          return;
        }

        r.bs.px = avg.px;
        r.bs.py = avg.py;
        r.bs.pz = avg.pz;
      });
    }
  }

  static convertToChars = (
    pitch: IMlbPitchExt,
    priority: BuildPriority,
    useDefaultPlate: boolean
  ): IConversionResult => {
    const warnings: string[] = [];

    try {
      const { releaseData, trajectoryData } = pitch;

      if (!releaseData) {
        throw new Error(`Failed to find releaseData of guid ${pitch.guid}`);
      }

      if (!trajectoryData) {
        throw new Error(`Failed to find trajectoryData of guid ${pitch.guid}`);
      }

      const safeSpinGlobal = _getReleaseSpin(releaseData);

      const spinAxisGlobal = BallHelper.safeNormalize(
        new Vector3(safeSpinGlobal.wx, safeSpinGlobal.wy, safeSpinGlobal.wz)
      );

      const heMatrix = HawkeyeHelper.getMatrix({
        Xx: releaseData.seamOrientation.xx,
        Xy: releaseData.seamOrientation.xy,
        Xz: releaseData.seamOrientation.xz,
        Yx: releaseData.seamOrientation.yx,
        Yy: releaseData.seamOrientation.yy,
        Yz: releaseData.seamOrientation.yz,
        Zx: releaseData.seamOrientation.zx,
        Zy: releaseData.seamOrientation.zy,
        Zz: releaseData.seamOrientation.zz,
      });

      if (!heMatrix) {
        throw new Error(
          `Failed to obtain Hawkeye matrix from pitchSegment of guid ${pitch.guid}`
        );
      }

      const safeSeams = HawkeyeHelper.getSeamsFromMatrix(
        heMatrix,
        spinAxisGlobal
      );

      const velos: Partial<IVelocity> = {
        vx: trajectoryData.trajectoryPolynomialX?.[1],
        vy: trajectoryData.trajectoryPolynomialY?.[1],
        vz: trajectoryData.trajectoryPolynomialZ?.[1],
      };

      if (isNil(velos.vx) || isNil(velos.vy) || isNil(velos.vz)) {
        throw new Error(
          `Failed to obtain velocity from trajectory polynomials of guid ${pitch.guid}`
        );
      }

      const trajVeloMPH = _getTrajVeloMPH({
        vx: velos.vx * FTPS_TO_MPH,
        vy: velos.vy * FTPS_TO_MPH,
        vz: velos.vz * FTPS_TO_MPH,
      });

      const safeSpinLocal = BallHelper.getLocalSpin(
        safeSpinGlobal,
        trajVeloMPH
      );

      const safeReleasePos = _getReleasePosition(releaseData);

      const safePlate = useDefaultPlate
        ? DEFAULT_PLATE
        : {
            plate_x: trajectoryData.location.x,
            plate_z: trajectoryData.location.z,
          };

      const output = PitchDesignHelper.deriveBsAndTraj(
        {
          speedMph: BallHelper.getSpeed(trajVeloMPH),
          spins: safeSpinLocal,
          spinExt: BallHelper.convertSpinToSpinExt(safeSpinLocal),
          breaks: {
            // flip from hawkeye frame of ref to trajekt frame of ref
            xInches: -trajectoryData.horizontalBreak * FT_TO_INCHES,
            zInches: trajectoryData.verticalBreakInduced * FT_TO_INCHES,
          },
          seams: safeSeams,
          plate: safePlate,
          releasePos: safeReleasePos,

          // not used by fn
          videoIDs: {
            windupVideoID: null,
            stretchVideoID: null,
          },
        },
        priority
      );

      return {
        guid: pitch.guid,
        bs: output.bs,
        traj: output.traj,
        seams: safeSeams,
        warnings: [],
      };
    } catch (e) {
      console.error(e);
      return {
        guid: '',
        bs: undefined,
        traj: undefined,
        seams: undefined,
        warnings: [],
      };
    }

    function _getTrajVeloMPH(rawOutput: IVelocity) {
      const LIMITS = BS_LIMITS.SPEED_MPH;

      const output: IVelocity = {
        vx: rawOutput.vx,
        vy: -clamp(Math.abs(rawOutput.vy), LIMITS.MIN, LIMITS.MAX),
        vz: rawOutput.vz,
      };

      if (Math.abs(output.vy) < Math.abs(rawOutput.vy)) {
        warnings.push(`Speed Y decreased to ${LIMITS.MAX} MPH`);
      } else if (Math.abs(output.vy) > Math.abs(rawOutput.vy)) {
        warnings.push(`Speed Y increased to ${LIMITS.MIN} MPH`);
      }

      return output;
    }

    function _getReleaseSpin(release: MlbReleaseData) {
      const LIMITS = BS_LIMITS.SPIN_RPM;

      const output: ISpin = {
        wx: clamp(release.spinVector.x, LIMITS.MIN, LIMITS.MAX),
        wy: clamp(release.spinVector.y, LIMITS.MIN, LIMITS.MAX),
        wz: clamp(release.spinVector.z, LIMITS.MIN, LIMITS.MAX),
      };

      if (output.wx < release.spinVector.x) {
        warnings.push(`Spin X decreased to ${LIMITS.MAX} RPM`);
      } else if (output.wx > release.spinVector.x) {
        warnings.push(`Spin X increased to ${LIMITS.MIN} RPM`);
      }

      if (output.wy < release.spinVector.y) {
        warnings.push(`Spin Y decreased to ${LIMITS.MAX} RPM`);
      } else if (output.wy > release.spinVector.y) {
        warnings.push(`Spin Y increased to ${LIMITS.MIN} RPM`);
      }

      if (output.wz < release.spinVector.z) {
        warnings.push(`Spin Z decreased to ${LIMITS.MAX} RPM`);
      } else if (output.wz > release.spinVector.z) {
        warnings.push(`Spin Z increased to ${LIMITS.MIN} RPM`);
      }

      return output;
    }

    function _getReleasePosition(release: MlbReleaseData) {
      const LIMITS = MS_LIMITS.POSITION;

      const output: IPosition = {
        px: clamp(release.releasePosition.x, LIMITS.X.MIN, LIMITS.X.MAX),
        py: release.releasePosition.y,
        pz: clamp(release.releasePosition.z, LIMITS.Z.MIN, LIMITS.Z.MAX),
      };

      if (output.px < release.releasePosition.x) {
        warnings.push(`Release X decreased to ${LIMITS.X.MAX}'`);
      } else if (output.px > release.releasePosition.x) {
        warnings.push(`Release X increased to ${LIMITS.X.MIN}'`);
      }

      if (output.pz < release.releasePosition.z) {
        warnings.push(`Release Z decreased to ${LIMITS.Z.MAX}'`);
      } else if (output.pz > release.releasePosition.z) {
        warnings.push(`Release Z increased to ${LIMITS.Z.MIN}'`);
      }

      return output;
    }
  };

  static averagePitches = (pitches: IMlbPitchExt[]) => {
    try {
      const dict: {
        [pitcherPk: string]: { [pitchType: string]: IMlbPitchExt[] };
      } = {};

      const safeGuids = pitches.filter(
        (g) =>
          // skip pitches with batters getting hit or intentional balls, etc...
          !AVG_EXCLUDED_OUTCOMES.includes(g.outcome ?? MlbOutcomeCode.NonPitch)
      );

      safeGuids.forEach((g) => {
        const pitcherPk = g.pitcherPk.toString();
        if (!pitcherPk) {
          return;
        }

        const pitchType = g.type;
        if (!pitchType) {
          return;
        }

        if (!dict[pitcherPk]) {
          dict[pitcherPk] = {};
        }

        if (!dict[pitcherPk][pitchType]) {
          dict[pitcherPk][pitchType] = [];
        }

        dict[pitcherPk][pitchType].push(g);
      });

      const output = Object.values(dict).flatMap((pitcher) =>
        Object.values(pitcher).flatMap((pitchType) => getAvgGuid(pitchType))
      );

      return output.filter((m) => !!m) as IMlbPitchExt[];
    } catch (e) {
      console.error(e);
    }

    function getAvgGuid(subset: IMlbPitchExt[]) {
      if (subset.length === 0) {
        return;
      }

      const releaseData = MiscHelper.getMeanObject(
        subset.map((g) => g.releaseData).filter((m) => !!m)
      ) as MlbReleaseData;

      if (!releaseData) {
        return;
      }

      const trajectoryData = MiscHelper.getMeanObject(
        subset.map((g) => g.trajectoryData).filter((m) => !!m)
      ) as MlbTrajectoryData;

      if (!trajectoryData) {
        return;
      }

      const trajPolys: { x: number[]; y: number[]; z: number[] }[] = [];

      subset
        .map((g) => g.trajectoryData)
        .forEach((data) => {
          if (!data) {
            return;
          }

          if (data.trajectoryPolynomialX?.length !== 3) {
            return;
          }

          if (data.trajectoryPolynomialY?.length !== 3) {
            return;
          }

          if (data.trajectoryPolynomialZ?.length !== 3) {
            return;
          }

          trajPolys.push({
            x: data.trajectoryPolynomialX,
            y: data.trajectoryPolynomialY,
            z: data.trajectoryPolynomialZ,
          });
        });

      // * custom overrides for specific, non-numeric props that can't be naively averaged
      trajectoryData.trajectoryPolynomialX = [
        MiscHelper.getMean(trajPolys.map((m) => m.x[0])),
        MiscHelper.getMean(trajPolys.map((m) => m.x[1])),
        MiscHelper.getMean(trajPolys.map((m) => m.x[2])),
      ];

      trajectoryData.trajectoryPolynomialY = [
        MiscHelper.getMean(trajPolys.map((m) => m.y[0])),
        MiscHelper.getMean(trajPolys.map((m) => m.y[1])),
        MiscHelper.getMean(trajPolys.map((m) => m.y[2])),
      ];

      trajectoryData.trajectoryPolynomialZ = [
        MiscHelper.getMean(trajPolys.map((m) => m.z[0])),
        MiscHelper.getMean(trajPolys.map((m) => m.z[1])),
        MiscHelper.getMean(trajPolys.map((m) => m.z[2])),
      ];

      trajectoryData.location = MiscHelper.getMeanObject(
        subset.map((g) => g.trajectoryData?.location).filter((m) => !!m)
      ) as MlbLocation;

      // if a strike exists in subset, choose that, otherwise use the first pitch
      const firstPitch =
        subset.find((s) =>
          STRIKE_OUTCOMES.includes(s.outcome as MlbOutcomeCode)
        ) ?? first(subset);

      if (!firstPitch) {
        return;
      }

      const safeSeam = subset.find((s) =>
        MlbStatsHelper.validSeamOrientation(s.releaseData?.seamOrientation)
      )?.releaseData?.seamOrientation;

      if (!safeSeam) {
        return;
      }

      const releasePosition = MiscHelper.getMeanObject(
        subset
          .filter((g) => g.releaseData?.releasePosition)
          .map((g) => g.releaseData?.releasePosition)
      ) as MlbReleasePosition;

      if (!releasePosition) {
        return;
      }

      const spinVector = MiscHelper.getMeanObject(
        subset
          .filter((g) => g.releaseData?.spinVector)
          .map((g) => g.releaseData?.spinVector)
      ) as MlbSpinVector;

      if (!spinVector) {
        return;
      }

      const output: IMlbPitchExt = {
        ...firstPitch,

        releaseData: {
          extension: releaseData.extension,
          angle: releaseData.angle,
          spinAxis: releaseData.spinAxis,
          spinRate: releaseData.spinRate,
          releaseSpeed: releaseData.releaseSpeed,
          direction: releaseData.direction,
          releasePosition: releasePosition,
          spinVector: spinVector,
          seamOrientation: safeSeam,
          spinFitError: 0, // * not used
          reducedConfidence: [], // * not used
        },

        trajectoryData: trajectoryData,

        _id: `avg-from-${firstPitch._id}`,

        samples: subset.length,
      };

      return output;
    }
  };

  static getRepertoire = (pitches: IMlbPitchExt[]) => {
    const averages = MlbStatsHelper.averagePitches(pitches) ?? [];

    const total = sum(averages.map((m) => m.samples ?? 0));

    const output: IRepertoire[] = averages.map((avg) => {
      const o: IRepertoire = {
        type: avg.type ?? '??',
        usage: ((avg.samples ?? 0) / total) * 100,
        speedMPH: avg.releaseData?.releaseSpeed ?? 0,

        breaks: {
          // to match how pitch list reports things (i.e. consistent after saving to PL)
          xInches: -(avg.trajectoryData?.horizontalBreak ?? 0) * FT_TO_INCHES,
          zInches:
            (avg.trajectoryData?.verticalBreakInduced ?? 0) * FT_TO_INCHES,
        },
      };

      return o;
    });

    output.sort((a, b) => (a.usage > b.usage ? -1 : 1));

    return output;
  };

  static mixinPlayerTeamDetails = (hitter: IHitter): IHitter => {
    if (!hitter.statsApiData) {
      return hitter;
    }

    return {
      ...hitter,
      _mx_mlb_team_details:
        MLB_TEAMS_DICT[hitter.statsApiData.teamPk.toString() ?? ''],
    };
  };
}
